/**
 * File Detail Slice
 *
 * Fetches file metadata via beaconApi.getFile().
 * Types and initial state in fileDetailState.ts.
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { FileDetailData, fileDetailInitialState } from './fileDetailState';
import { beaconApi } from '../../../services/beaconApi';
import type { RootState } from '../../store';

export const fetchFileDetail = createAsyncThunk<
  FileDetailData, string, { state: RootState; rejectValue: string }
>('fileDetail/fetch', async (fileName, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const result = await beaconApi.getFile(fileName, apiBase);
    if (!result?.file) return rejectWithValue(`File not found: ${fileName}`);
    return result.file as FileDetailData;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Network error');
  }
});

const fileDetailSlice = createSlice({
  name: 'fileDetail',
  initialState: fileDetailInitialState,
  reducers: { clearFileDetail: () => fileDetailInitialState },
  extraReducers: (b) => {
    b.addCase(fetchFileDetail.pending, (s) => { s.status = 'loading'; s.error = null; });
    b.addCase(fetchFileDetail.fulfilled, (s, a: PayloadAction<FileDetailData>) => { s.status = 'succeeded'; s.data = a.payload; });
    b.addCase(fetchFileDetail.rejected, (s, a) => { s.status = 'failed'; s.error = a.payload || 'Unknown error'; });
  },
});

export const { clearFileDetail } = fileDetailSlice.actions;
export default fileDetailSlice.reducer;
